import { ICarrier, ICustomer } from '@/types';
import React, { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form';
import {
  TextField,
  Grid,
  CircularProgress,
} from '@mui/material';
import api from '@/utils/axiosInterceptor';

const TruckDetails = () => {
  const { control, formState: { errors }, setValue, watch } =useFormContext<ICarrier | ICustomer>();
  const vinNumber = watch('truckDetails.vinNumber') || '';
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  useEffect(() => {
    const upper = String(vinNumber).toUpperCase().trim();
    if (upper.length !== 17) {
      setLookupError(null);
      return;
    }
    const decode = async () => {
      setLoading(true);
      setLookupError(null);
      try {
        const response = await api.get(`/customers/vin/${upper}`);
        const { data } = response.data || {};
        setValue('truckDetails.make', data?.make || '', { shouldValidate: true });
        setValue('truckDetails.model', data?.model || '', { shouldValidate: true });
        setValue('truckDetails.year', data?.year || '', { shouldValidate: true });
      } catch (err: any) {
        setLookupError(err?.response?.data?.message || 'Failed to decode VIN');
      } finally {
        setLoading(false);
      }
    };
    decode();
  }, [vinNumber, setValue]);

  return (
    <Grid container spacing={1.75}>
      <Grid item xs={12} md={6}>
        <Controller
          name="truckDetails.vinNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size='small'
              fullWidth
              label="VIN No"
              value={field.value || ''}
              onChange={(e) => {
                setValue('truckDetails.make', '', { shouldValidate: false });
                setValue('truckDetails.model', '', { shouldValidate: false });
                setValue('truckDetails.year', '', { shouldValidate: false });
                field.onChange(e.target.value.toUpperCase());
              }}
              error={!!errors.truckDetails?.vinNumber || !!lookupError}
              helperText={lookupError || errors.truckDetails?.vinNumber?.message}
              disabled={loading}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name="truckDetails.plateNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size='small'
              fullWidth
              label="Plate Number"
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.truckDetails?.plateNumber}
              helperText={errors.truckDetails?.plateNumber?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name="truckDetails.unitNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size='small'
              fullWidth
              label="Unit Number"
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.truckDetails?.unitNumber}
              helperText={errors.truckDetails?.unitNumber?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name="truckDetails.color"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size='small'
              fullWidth
              label="Color"
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.truckDetails?.color}
              helperText={errors.truckDetails?.color?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="truckDetails.make"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size='small'
              fullWidth
              label="Make"
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.truckDetails?.make}
              helperText={errors.truckDetails?.make?.message}
              disabled={loading}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="truckDetails.model"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size='small'
              fullWidth
              label="Model"
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.truckDetails?.model}
              helperText={errors.truckDetails?.model?.message}
              disabled={loading}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="truckDetails.year"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              size='small'
              fullWidth
              label="Year"
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              error={!!errors.truckDetails?.year}
              helperText={errors.truckDetails?.year?.message}
              disabled={loading}
            />
          )}
        />
      </Grid>
      {loading && (
        <Grid item xs={12}>
          <CircularProgress size={20} />
        </Grid>
      )}
    </Grid>
  );
};

export default TruckDetails